import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, BookOpen, Plus, Trash2, Sparkles, CheckCircle2, XCircle, Brain, ArrowRight, Camera, Target, TrendingUp, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function LernhilfenPage() {
  const { getAuthHeader, user } = useAuth();
  const [dailyTopics, setDailyTopics] = useState([]);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [topicEntries, setTopicEntries] = useState([{ subject_name: '', topic_name: '', notes: '' }]);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Selected topic for practice
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [topicQuestions, setTopicQuestions] = useState([]);
  
  // Quiz state
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);
  const [showQuiz, setShowQuiz] = useState(false);

  useEffect(() => {
    fetchDailyTopics();
    fetchQuizQuestions();
  }, []);

  const fetchDailyTopics = async () => {
    try {
      const headers = getAuthHeader();
      const response = await axios.get(`${BACKEND_URL}/api/daily-topics`, { headers });
      setDailyTopics(response.data || []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchQuizQuestions = async () => {
    try {
      const headers = getAuthHeader();
      const today = new Date().toISOString().split('T')[0];
      const response = await axios.get(`${BACKEND_URL}/api/quiz-questions?date=${today}`, { headers });
      setQuizQuestions(response.data || []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // Calculate stats
  const totalQuestions = quizQuestions.length;
  const answeredQuestions = quizQuestions.filter(q => q.is_correct !== null).length;
  const correctAnswers = quizQuestions.filter(q => q.is_correct === true).length;
  const progressPercent = totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0;

  // Get questions for a specific topic
  const getQuestionsForTopic = (topicId) => {
    return quizQuestions.filter(q => q.daily_topic_id === topicId);
  };

  // Get topic stats
  const getTopicStats = (topicId) => {
    const questions = getQuestionsForTopic(topicId);
    const total = questions.length;
    const correct = questions.filter(q => q.is_correct === true).length;
    const answered = questions.filter(q => q.is_correct !== null).length;
    return { total, correct, answered, percent: total > 0 ? Math.round((correct / total) * 100) : 0 };
  };

  const addTopicEntry = () => {
    setTopicEntries([...topicEntries, { subject_name: '', topic_name: '', notes: '' }]);
  };

  const removeTopicEntry = (index) => {
    if (topicEntries.length > 1) {
      setTopicEntries(topicEntries.filter((_, i) => i !== index));
    }
  };

  const updateTopicEntry = (index, field, value) => {
    const updated = [...topicEntries];
    updated[index][field] = value;
    setTopicEntries(updated);
  };

  const handleSaveTopics = async () => {
    const validTopics = topicEntries.filter(t => t.subject_name.trim() && t.topic_name.trim());
    if (validTopics.length === 0) {
      toast.error('Bitte gib mindestens ein Fach und Thema ein');
      return;
    }

    setSaving(true);
    try {
      const headers = getAuthHeader();
      await axios.post(`${BACKEND_URL}/api/daily-topics`, { topics: validTopics }, { headers });
      toast.success(`${validTopics.length} Themen gespeichert! 📚`);
      fetchDailyTopics();
      setTopicEntries([{ subject_name: '', topic_name: '', notes: '' }]);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateQuiz = async (topicId) => {
    setGenerating(true);
    try {
      const headers = getAuthHeader();
      const response = await axios.post(`${BACKEND_URL}/api/daily-topics/${topicId}/generate-quiz`, {}, { headers });
      toast.success(`${response.data.questions_generated} Aufgaben erstellt! 🎉`);
      fetchQuizQuestions();
      fetchDailyTopics();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Fehler beim Erstellen');
    } finally {
      setGenerating(false);
    }
  };

  const handleImageUpload = async (event, existingTopicId = null) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const headers = getAuthHeader();
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${BACKEND_URL}/api/generate-from-image-topic`,
        formData,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' }, timeout: 120000 }
      );

      toast.success(`${response.data.questions_generated} Aufgaben aus Bild erstellt! 📸`);
      fetchDailyTopics();
      fetchQuizQuestions();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Fehler beim Verarbeiten des Bildes');
    } finally {
      setUploading(false);
    }
  };

  // Start practicing a specific topic
  const startTopicPractice = (topic) => {
    const questions = getQuestionsForTopic(topic.id);
    const unanswered = questions.filter(q => q.is_correct === null);
    
    if (unanswered.length === 0 && questions.length > 0) {
      toast.info('Alle Aufgaben bereits bearbeitet! 🎉');
      return;
    }
    
    if (questions.length === 0) {
      toast.error('Keine Aufgaben für dieses Thema. Erstelle zuerst Aufgaben!');
      return;
    }
    
    setSelectedTopic(topic);
    setTopicQuestions(questions);
    setCurrentQuestionIndex(0);
    setCurrentQuestion(unanswered[0] || questions[0]);
    setShowQuiz(true);
    setUserAnswer('');
    setResult(null);
  };

  const handleCheckAnswer = async () => {
    if (!userAnswer.trim()) {
      toast.error('Bitte gib eine Antwort ein');
      return;
    }

    setChecking(true);
    try {
      const headers = getAuthHeader();
      const response = await axios.post(
        `${BACKEND_URL}/api/quiz-questions/${currentQuestion.id}/answer?user_answer=${encodeURIComponent(userAnswer)}`,
        {},
        { headers }
      );

      setResult(response.data);
      
      if (response.data.is_correct) {
        toast.success(`Richtig! +10 Punkte 🎉`);
      }
      
      fetchQuizQuestions();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Fehler beim Prüfen');
    } finally {
      setChecking(false);
    }
  };

  const nextQuestion = () => {
    const unanswered = topicQuestions.filter(q => q.is_correct === null && q.id !== currentQuestion.id);
    
    if (unanswered.length > 0) {
      setCurrentQuestion(unanswered[0]);
      setCurrentQuestionIndex(prev => prev + 1);
      setUserAnswer('');
      setResult(null);
    } else {
      // All done for this topic
      setShowQuiz(false);
      setSelectedTopic(null);
      fetchQuizQuestions();
      toast.success('Thema abgeschlossen! 🏆');
    }
  };

  const exitQuiz = () => {
    setShowQuiz(false);
    setSelectedTopic(null);
    setCurrentQuestion(null);
    setResult(null);
    setUserAnswer('');
    fetchQuizQuestions();
  };

  const today = new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' });

  // Quiz Mode
  if (showQuiz && currentQuestion && selectedTopic) {
    const topicStats = getTopicStats(selectedTopic.id);
    
    return (
      <div className="space-y-6">
        {/* Header with topic info */}
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-3xl p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-white/80 text-sm">Aktives Thema</p>
              <h2 className="text-2xl font-heading font-bold">{selectedTopic.subject_name}: {selectedTopic.topic_name}</h2>
            </div>
            <Button onClick={exitQuiz} variant="outline" className="bg-white/20 border-white/30 text-white hover:bg-white/30">
              Beenden
            </Button>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="flex justify-between text-sm mb-1">
                <span>Fortschritt</span>
                <span>{topicStats.correct}/{topicStats.total} richtig</span>
              </div>
              <Progress value={topicStats.percent} className="h-3 bg-white/20" />
            </div>
            <div className="text-3xl font-bold">{topicStats.percent}%</div>
          </div>
        </div>

        {/* Question Card */}
        <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm">
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <span className="px-3 py-1 bg-primary-600 text-white text-sm font-bold rounded-full">
                {currentQuestion.subject_name}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                currentQuestion.difficulty === 'hard' ? 'bg-red-100 text-red-700' :
                currentQuestion.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                'bg-green-100 text-green-700'
              }`}>
                {currentQuestion.difficulty === 'hard' ? 'Schwer' : currentQuestion.difficulty === 'medium' ? 'Mittel' : 'Einfach'}
              </span>
              <span className="px-3 py-1 bg-slate-100 text-slate-600 text-sm rounded-full">
                Frage {currentQuestionIndex + 1}
              </span>
            </div>
            
            <h3 className="text-2xl font-heading font-bold text-slate-900 mb-4">Aufgabe</h3>
            <p className="text-lg text-slate-700 leading-relaxed whitespace-pre-wrap">
              {currentQuestion.question}
            </p>
          </div>

          {!result ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Deine Antwort:</label>
                <Textarea
                  value={userAnswer}
                  onChange={(e) => setUserAnswer(e.target.value)}
                  placeholder="Schreibe hier deine Antwort..."
                  className="min-h-[120px] rounded-2xl"
                  data-testid="answer-input"
                />
              </div>
              <Button
                onClick={handleCheckAnswer}
                disabled={checking || !userAnswer.trim()}
                className="w-full h-12 rounded-full bg-primary-600 hover:bg-primary-700 text-white font-bold"
                data-testid="check-answer-btn"
              >
                {checking ? 'Prüfe...' : 'Antwort prüfen'}
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className={`p-6 rounded-2xl ${result.is_correct ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'}`}>
                <div className="flex items-center gap-3 mb-3">
                  {result.is_correct ? (
                    <>
                      <CheckCircle2 className="text-green-600" size={32} />
                      <div>
                        <p className="text-xl font-heading font-bold text-green-900">Richtig! 🎉</p>
                        <p className="text-sm text-green-700">+10 Punkte</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <XCircle className="text-red-600" size={32} />
                      <p className="text-xl font-heading font-bold text-red-900">Nicht ganz richtig</p>
                    </>
                  )}
                </div>
                <p className="text-slate-700">{result.feedback}</p>
              </div>

              <div className="p-6 bg-blue-50 rounded-2xl border border-blue-200">
                <p className="font-bold text-blue-900 mb-2">Korrekte Antwort:</p>
                <p className="text-blue-800 mb-4 whitespace-pre-wrap">{result.correct_answer}</p>
                <p className="font-bold text-blue-900 mb-2">Erklärung:</p>
                <p className="text-blue-700 text-sm whitespace-pre-wrap">{result.explanation}</p>
              </div>

              <Button
                onClick={nextQuestion}
                className="w-full h-12 rounded-full bg-primary-600 hover:bg-primary-700 text-white font-bold"
                data-testid="next-question-btn"
              >
                <ArrowRight size={20} className="mr-2" />
                Nächste Aufgabe
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Main View
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-heading font-bold text-slate-900 mb-2">Tägliche Lernhilfen</h1>
        <p className="text-lg text-slate-600">{today}</p>
      </div>

      {/* Tages-Thermometer */}
      <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-3xl p-6 text-white">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp size={24} />
          <h2 className="text-xl font-heading font-bold">Dein Tages-Thermometer 🌡️</h2>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 text-center">
            <p className="text-3xl font-bold">{dailyTopics.length}</p>
            <p className="text-sm text-white/80">Themen heute</p>
          </div>
          <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 text-center">
            <p className="text-3xl font-bold">{totalQuestions}</p>
            <p className="text-sm text-white/80">Aufgaben erstellt</p>
          </div>
          <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 text-center">
            <p className="text-3xl font-bold">{correctAnswers}</p>
            <p className="text-sm text-white/80">Richtig beantwortet</p>
          </div>
          <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 text-center">
            <p className="text-3xl font-bold">{progressPercent}%</p>
            <p className="text-sm text-white/80">Erfolgsquote</p>
          </div>
        </div>
        
        <div>
          <div className="flex justify-between text-sm mb-2">
            <span>Gesamtfortschritt heute</span>
            <span>{correctAnswers} von {totalQuestions} Aufgaben</span>
          </div>
          <div className="h-4 bg-white/20 rounded-full overflow-hidden">
            <div 
              className="h-full bg-white rounded-full transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Neues Thema hinzufügen */}
        <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm">
          <h2 className="text-xl font-heading font-bold text-slate-900 mb-4">
            <Plus className="inline mr-2" size={20} />
            Neues Thema hinzufügen
          </h2>
          
          <div className="space-y-4">
            {topicEntries.map((entry, index) => (
              <div key={index} className="p-4 bg-slate-50 rounded-2xl space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-slate-500">Thema {index + 1}</span>
                  {topicEntries.length > 1 && (
                    <button onClick={() => removeTopicEntry(index)} className="text-red-500 hover:text-red-700">
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    placeholder="Fach (z.B. Physik)"
                    value={entry.subject_name}
                    onChange={(e) => updateTopicEntry(index, 'subject_name', e.target.value)}
                    className="rounded-xl"
                    data-testid={`subject-input-${index}`}
                  />
                  <Input
                    placeholder="Thema (z.B. Optik)"
                    value={entry.topic_name}
                    onChange={(e) => updateTopicEntry(index, 'topic_name', e.target.value)}
                    className="rounded-xl"
                    data-testid={`topic-input-${index}`}
                  />
                </div>
              </div>
            ))}
            
            <div className="flex gap-3">
              <Button onClick={addTopicEntry} variant="outline" className="flex-1 rounded-full">
                <Plus size={18} className="mr-2" /> Weiteres Thema
              </Button>
              <Button onClick={handleSaveTopics} disabled={saving} className="flex-1 rounded-full bg-primary-600 hover:bg-primary-700 text-white">
                {saving ? 'Speichere...' : 'Speichern'}
              </Button>
            </div>

            {/* Image Upload */}
            <div className="pt-4 border-t border-slate-200">
              <p className="text-sm font-medium text-slate-700 mb-3">Oder lade ein Bild hoch:</p>
              <input
                type="file"
                accept="image/*,.heic,.heif"
                onChange={handleImageUpload}
                className="hidden"
                id="image-upload"
                disabled={uploading}
              />
              <label
                htmlFor="image-upload"
                className="flex items-center justify-center gap-3 w-full p-4 border-2 border-dashed border-slate-300 rounded-2xl cursor-pointer hover:border-primary-500 hover:bg-primary-50 transition-all"
              >
                {uploading ? (
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary-600 border-t-transparent mx-auto mb-2"></div>
                    <p className="text-sm text-slate-600">KI analysiert Bild...</p>
                  </div>
                ) : (
                  <div className="text-center">
                    <Camera className="mx-auto text-slate-400 mb-1" size={24} />
                    <p className="font-medium text-sm text-slate-700">Foto von Schulbuch/Arbeitsblatt</p>
                    <p className="text-xs text-slate-500">JPG, PNG oder HEIC (iPhone)</p>
                  </div>
                )}
              </label>
            </div>
          </div>
        </div>

        {/* Deine Themen heute */}
        <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm">
          <h2 className="text-xl font-heading font-bold text-slate-900 mb-4">
            <BookOpen className="inline mr-2" size={20} />
            Deine Themen heute
          </h2>
          
          {dailyTopics.length === 0 ? (
            <div className="flex items-center justify-center h-48 text-center">
              <div>
                <BookOpen className="mx-auto text-slate-300 mb-4" size={48} />
                <p className="text-slate-600">Füge links ein Thema hinzu oder lade ein Bild hoch!</p>
              </div>
            </div>
          ) : (
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {dailyTopics.map(topic => {
                const stats = getTopicStats(topic.id);
                const isComplete = stats.total > 0 && stats.correct === stats.total;
                
                return (
                  <div 
                    key={topic.id} 
                    className={`p-4 rounded-2xl border-2 transition-all ${
                      isComplete 
                        ? 'bg-green-50 border-green-200' 
                        : stats.total > 0 
                          ? 'bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200 hover:border-indigo-400 cursor-pointer'
                          : 'bg-slate-50 border-slate-200'
                    }`}
                    onClick={() => stats.total > 0 && !isComplete && startTopicPractice(topic)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="px-2 py-0.5 bg-primary-600 text-white text-xs font-bold rounded-full">
                            {topic.subject_name}
                          </span>
                          {isComplete && (
                            <span className="px-2 py-0.5 bg-green-600 text-white text-xs font-bold rounded-full">
                              ✓ Fertig
                            </span>
                          )}
                        </div>
                        <p className="font-bold text-slate-900">{topic.topic_name}</p>
                      </div>
                      
                      {!topic.quiz_generated ? (
                        <Button
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleGenerateQuiz(topic.id); }}
                          disabled={generating}
                          className="rounded-full bg-primary-600 hover:bg-primary-700 text-white"
                        >
                          <Sparkles size={14} className="mr-1" />
                          Aufgaben erstellen
                        </Button>
                      ) : stats.total > 0 && !isComplete ? (
                        <Button
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); startTopicPractice(topic); }}
                          className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white"
                        >
                          <Target size={14} className="mr-1" />
                          Üben
                        </Button>
                      ) : null}
                    </div>
                    
                    {stats.total > 0 && (
                      <div>
                        <div className="flex justify-between text-xs text-slate-600 mb-1">
                          <span>{stats.correct} von {stats.total} richtig</span>
                          <span>{stats.percent}%</span>
                        </div>
                        <Progress value={stats.percent} className="h-2" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Anleitung */}
      <div className="bg-gradient-to-br from-slate-100 to-slate-200 rounded-3xl p-6">
        <h3 className="text-lg font-heading font-bold text-slate-900 mb-3">💡 So funktioniert's</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
          <div className="bg-white rounded-2xl p-4">
            <p className="font-bold text-primary-600 mb-1">1. Thema eintragen</p>
            <p className="text-slate-600">Schreibe Fach und Thema oder lade ein Foto hoch</p>
          </div>
          <div className="bg-white rounded-2xl p-4">
            <p className="font-bold text-primary-600 mb-1">2. Aufgaben erstellen</p>
            <p className="text-slate-600">Die KI erstellt passende Übungsaufgaben</p>
          </div>
          <div className="bg-white rounded-2xl p-4">
            <p className="font-bold text-primary-600 mb-1">3. Thema auswählen</p>
            <p className="text-slate-600">Klicke auf ein Thema zum Üben</p>
          </div>
          <div className="bg-white rounded-2xl p-4">
            <p className="font-bold text-primary-600 mb-1">4. 100% erreichen</p>
            <p className="text-slate-600">Beantworte alle Fragen richtig!</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LernhilfenPage;
